import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { pb } from '@/lib/pocketbase';
import { getProfilePictureUrl } from '@/utils/profileUtils';
import { X, Upload, MapPin, Calendar, Camera, Plus, Sparkles, Video, Play } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import StoryTemplates from './StoryTemplates';
import StoryInteractiveFeatures from './StoryInteractiveFeatures';

interface AddTravelStoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddTravelStoryModal = ({ isOpen, onClose, onSuccess }: AddTravelStoryModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    caption: '',
    location: '',
    tags: [] as string[],
    image: null as File | null,
    videos: [] as File[],
    interactiveElements: [] as any[],
    templateId: '',
    layoutData: null as any,
    canvasSettings: null as any,
    textOverlays: [] as any[],
    imageFilters: [] as any[],
    stickers: [] as any[],
    editedImages: [] as any[]
  });
  const [currentTag, setCurrentTag] = useState('');
  const [imagePreview, setImagePreview] = useState<string>('');
  const [videoPreviews, setVideoPreviews] = useState<string[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    processMediaFiles(files);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Prevent uploading image if video already exists
    if (formData.videos.length > 0) {
      toast({
        title: "Media Limit",
        description: "Please remove the video first to upload an image",
        variant: "destructive"
      });
      return;
    }

    const file = files[0];
    
    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File Type",
        description: "Only image files are allowed (JPG, PNG, WEBP)",
        variant: "destructive"
      });
      return;
    }

    // Check file size (10MB max for image)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Image size must be 10MB or less",
        variant: "destructive"
      });
      return;
    }

    // Add the image
    setFormData(prev => ({
      ...prev,
      image: file
    }));

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    toast({
      title: "Image Added",
      description: "Your image has been successfully added",
    });
  };

  const processMediaFiles = async (files: File[]) => {
    console.log('📹 Video upload started, files:', files.length);
    
    if (files.length === 0) {
      console.log('❌ No files selected');
      return;
    }

    // Prevent uploading video if image already exists
    if (formData.image) {
      toast({
        title: "Media Limit",
        description: "Please remove the image first to upload a video",
        variant: "destructive"
      });
      return;
    }

    // Only allow one video
    if (formData.videos.length >= 1) {
      toast({
        title: "Video Limit Reached",
        description: "Only one video is allowed per story",
        variant: "destructive"
      });
      return;
    }

    // Only process the first file
    const file = files[0];
    console.log('📹 Processing file:', file.name, file.type, file.size);
    
    // Check if it's a video
    if (!file.type.startsWith('video/')) {
      toast({
        title: "Invalid File Type",
        description: "Only video files are allowed (MP4, MOV)",
        variant: "destructive"
      });
      return;
    }

    // Check file format (MP4 or MOV)
    const validFormats = ['video/mp4', 'video/quicktime'];
    if (!validFormats.includes(file.type)) {
      toast({
        title: "Invalid Video Format",
        description: `The video format "${file.type}" is not supported. Please upload MP4 or MOV format only.`,
        variant: "destructive"
      });
      return;
    }

    // Check file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB in bytes
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      toast({
        title: "Video Too Large",
        description: `The video size is ${fileSizeMB}MB. Maximum allowed size is 50MB. Please compress your video or upload a shorter one.`,
        variant: "destructive"
      });
      return;
    }

    // Check video duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = function() {
      window.URL.revokeObjectURL(video.src);
      const duration = video.duration;
      
      // Max 5 minutes (300 seconds)
      if (duration > 300) {
        toast({
          title: "Video Too Long",
          description: `The video duration is ${Math.floor(duration / 60)} minutes ${Math.floor(duration % 60)} seconds. Maximum allowed duration is 5 minutes. Please trim your video.`,
          variant: "destructive"
        });
        return;
      }
      
      // If all checks pass, proceed with upload
      proceedWithVideoUpload(file);
    };

    video.onerror = function() {
      toast({
        title: "Video Error",
        description: "Unable to read video file. The file may be corrupted or in an unsupported format.",
        variant: "destructive"
      });
    };

    video.src = URL.createObjectURL(file);
  };

  const proceedWithVideoUpload = (file: File) => {

    console.log('✅ File passed initial validation, checking duration...');

    // Check video duration with timeout
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    let timeoutId: NodeJS.Timeout;
    let metadataLoaded = false;

    const cleanup = () => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(video.src);
    };

    video.onloadedmetadata = function() {
      if (metadataLoaded) return;
      metadataLoaded = true;
      
      console.log('📹 Video metadata loaded');
      const duration = video.duration;
      console.log('⏱️ Video duration:', duration);

      cleanup();

      if (duration > 20) {
        toast({
          title: "Video Too Long",
          description: "Video duration must be 20 seconds or less",
          variant: "destructive"
        });
        return;
      }

      console.log('✅ Duration check passed, adding video to form');
      addVideoToForm(file);
    };

    video.onerror = function(e) {
      if (metadataLoaded) return;
      metadataLoaded = true;
      
      console.warn('⚠️ Could not load video metadata, adding without duration check:', e);
      cleanup();
      
      // Add the video anyway since metadata loading failed but file format is valid
      addVideoToForm(file);
    };

    // Timeout after 3 seconds - add video anyway if metadata doesn't load
    timeoutId = setTimeout(() => {
      if (metadataLoaded) return;
      metadataLoaded = true;
      
      console.warn('⚠️ Video metadata loading timeout, adding without duration check');
      cleanup();
      
      // Add the video anyway
      addVideoToForm(file);
    }, 3000);

    const videoUrl = URL.createObjectURL(file);
    console.log('📹 Created video URL:', videoUrl);
    video.src = videoUrl;
  };

  const addVideoToForm = (file: File) => {
    // All validations passed, add the video
    setFormData(prev => ({
      ...prev,
      videos: [file]
    }));

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      console.log('✅ Video preview generated, length:', result?.length);
      setVideoPreviews([result]);
    };
    reader.readAsDataURL(file);

    toast({
      title: "Video Added",
      description: "Your video has been successfully added",
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processMediaFiles(files);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null
    }));
    setImagePreview('');
  };

  const removeVideo = (index: number) => {
    setFormData(prev => ({
      ...prev,
      videos: []
    }));
    setVideoPreviews([]);
  };

  const handleSelectTemplate = (template: any) => {
    console.log('Template selected in AddTravelStoryModal:', template.name);
    setSelectedTemplate(template);
    
    // Apply template data to form
    setFormData(prev => ({
      ...prev,
      templateId: template.id,
      layoutData: template.layout,
      textOverlays: [{
        id: Date.now().toString(),
        text: prev.title || 'Your Story Title',
        x: template.layout.textPosition === 'center' ? 50 : 
           template.layout.textPosition === 'top' ? 50 : 
           template.layout.textPosition === 'bottom' ? 50 : 20,
        y: template.layout.textPosition === 'center' ? 50 : 
           template.layout.textPosition === 'top' ? 15 : 
           template.layout.textPosition === 'bottom' ? 80 : 25,
        fontSize: template.layout.textStyle.fontSize,
        fontWeight: template.layout.textStyle.fontWeight,
        color: template.layout.textStyle.color,
        backgroundColor: template.layout.textStyle.backgroundColor || 'transparent',
        borderRadius: template.layout.textStyle.borderRadius || 0,
        padding: template.layout.textStyle.padding || 0
      }],
      stickers: template.layout.decorations.filter((d: any) => d.type === 'icon').map((decoration: any) => ({
        id: Date.now().toString() + Math.random(),
        type: 'emoji',
        content: decoration.icon,
        x: decoration.position.x,
        y: decoration.position.y,
        size: decoration.size || 24
      }))
    }));
    
    setShowTemplates(false);
    
    toast({
      title: "Template Applied!",
      description: `${template.name} template has been applied to your story.`
    });
  };

  const handleAddInteractiveElement = (element: any) => {
    setFormData(prev => ({
      ...prev,
      interactiveElements: [...prev.interactiveElements, element]
    }));
  };

  const handleRemoveInteractiveElement = (id: string) => {
    setFormData(prev => ({
      ...prev,
      interactiveElements: prev.interactiveElements.filter(el => el.id !== id)
    }));
  };

  const addTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      if (formData.tags.length >= 10) {
        toast({
          title: "Tag Limit",
          description: "Maximum 10 tags allowed",
          variant: "destructive"
        });
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to create a story",
        variant: "destructive"
      });
      return;
    }

    // Check if tutor has a profile picture
    try {
      const tutorProfiles = await pb.collection('tutors').getList(1, 1, {
        filter: `user = "${user.id}"`
      });
      
      if (tutorProfiles.items.length > 0) {
        const profile = tutorProfiles.items[0];
        if (!getProfilePictureUrl(profile)) {
          toast({
            title: "Complete your profile first",
            description: "You need to add a profile picture before creating travel stories.",
            variant: "destructive"
          });
          onClose();
          return;
        }
      } else {
        toast({
          title: "Profile not found",
          description: "Please complete your tutor profile first.",
          variant: "destructive"
        });
        onClose();
        return;
      }
    } catch (error) {
      console.error('Error checking tutor profile:', error);
      toast({
        title: "Error",
        description: "Failed to verify your profile. Please try again.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.title.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in the title field",
        variant: "destructive"
      });
      return;
    }

    if (!formData.image && formData.videos.length === 0) {
      toast({
        title: "Media Required",
        description: "Please upload either an image or a video for your story",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // First, get the tutor profile ID
      let tutorProfile;
      try {
        tutorProfile = await pb.collection('tutors').getFirstListItem(`user="${user?.id}"`);
      } catch (tutorError) {
        console.error('Error finding tutor profile:', tutorError);
        toast({
          title: "Profile Not Found",
          description: "Could not find your tutor profile. Please contact support.",
          variant: "destructive"
        });
        return;
      }

      // Get the next display_order
      let displayOrder = 1;
      try {
        const allStories = await pb.collection('travel_stories').getList(1, 1, {
          sort: '-display_order',
          fields: 'display_order'
        });
        const maxOrder = allStories.items[0]?.display_order || 0;
        displayOrder = maxOrder + 1;
        console.log('📊 Next display_order:', displayOrder);
      } catch (error) {
        console.error('Error fetching max display_order:', error);
        displayOrder = 1;
      }

      // Create the story data object
      const storyData = {
        title: formData.title,
        caption: formData.caption,
        location: formData.location || '',
        tags: formData.tags,
        tutorId: tutorProfile.id,
        likes: 0,
        views: 0,
        isPublished: true,
        interactiveElements: formData.interactiveElements,
        templateId: formData.templateId || '',
        layoutData: formData.layoutData,
        canvasSettings: formData.canvasSettings,
        textOverlays: formData.textOverlays,
        imageFilters: formData.imageFilters,
        stickers: formData.stickers,
        editedImages: formData.editedImages,
        display_order: displayOrder
      };

      // Create FormData for file upload
      const formDataForUpload = new FormData();
      
      // Add all fields
      Object.entries(storyData).forEach(([key, value]) => {
        if (key === 'tags' || key === 'interactiveElements' || key === 'layoutData' || 
            key === 'canvasSettings' || key === 'textOverlays' || key === 'imageFilters' || 
            key === 'stickers' || key === 'editedImages') {
          console.log(`📋 Adding ${key} as JSON:`, value);
          formDataForUpload.append(key, JSON.stringify(value));
        } else {
          formDataForUpload.append(key, String(value));
        }
      });

      // Add image
      if (formData.image) {
        formDataForUpload.append('images', formData.image);
      }

      // Add videos
      formData.videos.forEach((video) => {
        formDataForUpload.append('videos', video);
      });

      console.log('🎯 Interactive Elements being sent:', formData.interactiveElements);
      console.log('📤 Full story data being sent:', storyData);

      try {
        const createdStory = await pb.collection('travel_stories').create(formDataForUpload);
        console.log('✅ Story created successfully:', createdStory);
      } catch (error) {
        console.error('❌ PocketBase error:', error);
        console.error('Error details:', error.response?.data || error.message);
        throw error;
      }
      
      toast({
        title: "Story Created!",
        description: "Your travel story has been created successfully"
      });
      
      // Reset form
      setFormData({
        title: '',
        caption: '',
        location: '',
        tags: [],
        image: null,
        videos: [],
        interactiveElements: [],
        templateId: '',
        layoutData: null,
        canvasSettings: null,
        textOverlays: [],
        imageFilters: [],
        stickers: [],
        editedImages: []
      });
      setImagePreview('');
      setVideoPreviews([]);
      setCurrentTag('');

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error creating story:', error);
      
      let errorMessage = "Failed to create story. Please try again.";
      
      if (error.status === 400 && error.data?.data) {
        const validationErrors = error.data.data;
        const errorFields = Object.keys(validationErrors);
        if (errorFields.length > 0) {
          const fieldErrors = errorFields.map(field => {
            const fieldError = validationErrors[field];
            if (typeof fieldError === 'object' && fieldError.message) {
              return `${field}: ${fieldError.message}`;
            }
            return `${field}: ${fieldError}`;
          }).join(', ');
          errorMessage = `Validation errors: ${fieldErrors}`;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl max-h-[85vh] overflow-y-auto top-[5%] translate-y-0"
        onInteractOutside={(e) => {
          // Prevent closing when interacting with template modal
          if (showTemplates) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Create Travel Story
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              placeholder="e.g., Amazing Adventure in Bali"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
            />
          </div>

          {/* Caption */}
          <div>
            <Label htmlFor="caption">Story Caption</Label>
            <Textarea
              id="caption"
              placeholder="Tell your story... What made this journey special?"
              value={formData.caption}
              onChange={(e) => handleInputChange('caption', e.target.value)}
              className="min-h-[120px]"
            />
          </div>

          {/* Location */}
          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              placeholder="Where was this taken? (e.g., Paris, France)"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
            />
          </div>

          {/* Media Upload Header */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Camera className="h-5 w-5 text-blue-600" />
              <Video className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              Choose Photo or Video
            </h3>
            <p className="text-sm text-gray-600">
              Upload either a photo or a video for your story (not both)
            </p>
          </div>

          {/* Image Upload Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-gray-700">
                Story Image
                {formData.videos.length > 0 && (
                  <Badge variant="secondary" className="ml-2">Video uploaded</Badge>
                )}
              </Label>
            </div>
            
            {formData.videos.length > 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">
                  Remove the video to upload an image instead
                </p>
              </div>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs text-green-800">
                    Upload an image for your story (JPG, PNG, WEBP • Max 10MB)
                  </p>
                </div>

            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleImageChange}
              className="hidden"
            />

            {/* Image Upload Area */}
            {!formData.image && (
              <div
                className="border-2 border-dashed rounded-xl p-6 transition-all duration-200 cursor-pointer hover:border-green-400 hover:bg-green-50/50 border-gray-300 bg-gray-50"
                onClick={() => imageInputRef.current?.click()}
              >
                <div className="text-center space-y-2">
                  <div className="mx-auto w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-base font-medium text-gray-800">
                    Upload Story Image
                  </p>
                  <p className="text-xs text-gray-500">
                    JPG, PNG, or WEBP • Max 10MB
                  </p>
                </div>
              </div>
            )}

            {/* Image Preview */}
            {imagePreview && (
              <div className="max-w-md mx-auto">
                <div className="relative group rounded-xl overflow-hidden">
                  <img 
                    src={imagePreview}
                    alt="Story preview"
                    className="w-full rounded-lg"
                  />
                  <div className="absolute top-2 right-2">
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={removeImage}
                      className="bg-red-500/80 backdrop-blur-sm hover:bg-red-600/90"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {/* Video Upload Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-gray-700">
                Video Upload
                {formData.image && (
                  <Badge variant="secondary" className="ml-2">Image uploaded</Badge>
                )}
              </Label>
            </div>
            
            {formData.image ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">
                  Remove the image to upload a video instead
                </p>
              </div>
            ) : (
              <>
                {/* Upload Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Upload Requirements</h4>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Upload either an image or a video (not both)</li>
                    <li>• Maximum length: 20 seconds</li>
                    <li>• Maximum file size: 50 MB</li>
                    <li>• Accepted formats: MP4, MOV</li>
                    <li>• Upload one video at a time</li>
                    <li>• Videos longer or larger than allowed will be rejected</li>
                  </ul>
                </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime"
              onChange={handleMediaChange}
              className="hidden"
            />

            {/* Upload Area */}
            {formData.videos.length === 0 && (
              <div
                className={`border-2 border-dashed rounded-xl p-8 transition-all duration-200 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 ${
                  isDragOver 
                    ? 'border-blue-500 bg-blue-50 scale-[1.02]' 
                    : 'border-gray-300 bg-gray-50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleUploadClick}
              >
                <div className="text-center space-y-3">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    {isDragOver ? (
                      <Video className="h-8 w-8 text-white animate-bounce" />
                    ) : (
                      <Upload className="h-8 w-8 text-white" />
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-lg font-medium text-gray-800">
                      {isDragOver 
                        ? 'Drop your video here' 
                        : 'Click to upload or drag and drop'
                      }
                    </p>
                    <p className="text-sm text-gray-500">
                      MP4 or MOV • Max 20 seconds • Max 50MB
                    </p>
                  </div>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    className="mt-3 bg-white hover:bg-purple-50 border-purple-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUploadClick();
                    }}
                  >
                    <Video className="h-4 w-4 mr-2" />
                    Choose Video
                  </Button>
                </div>
              </div>
            )}

            {/* Video Preview */}
            {videoPreviews.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">
                  Video Preview
                </Label>
                <div className="max-w-md mx-auto">
                  {/* Video Preview */}
                  <div className="relative group bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                    <div className="aspect-video">
                      <video
                        src={videoPreviews[0]}
                        className="w-full h-full object-cover"
                        controls
                        playsInline
                      />
                    </div>
                    
                    {/* Overlay with actions */}
                    <div className="absolute top-2 right-2">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeVideo(0);
                        }}
                        className="bg-red-500/80 backdrop-blur-sm hover:bg-red-600/90 transition-colors"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {/* Tags */}
          <div>
            <Label>Tags</Label>
            <div className="flex gap-2 mt-1">
              <Input
                placeholder="Add a tag (e.g., Adventure, Culture, Food)"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1"
              />
              <Button type="button" onClick={addTag} variant="outline" size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="px-2 py-1">
                    #{tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Interactive Features */}
          <div>
            <StoryInteractiveFeatures
              onAddElement={handleAddInteractiveElement}
              elements={formData.interactiveElements}
              onRemoveElement={handleRemoveInteractiveElement}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Upload className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Camera className="mr-2 h-4 w-4" />
                  Create Story
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>

    </Dialog>
  );
};

export default AddTravelStoryModal;